import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';

const CompanionName: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [name, setName] = useState('');

  useEffect(() => {
    // Check companion limit before starting creation flow
    const checkCompanionLimit = async () => {
      try {
        const [companionsRes, usageRes] = await Promise.all([
          axios.get('/companions'),
          axios.get('/users/usage').catch(() => null),
        ]);
        const count = companionsRes.data?.length || 0;
        const maxCompanions = usageRes?.data?.maxCompanions || 1;

        if (count >= maxCompanions) {
          toast.error(
            t(
              'companionCreation.limitReached',
              'You have reached your companion limit. Upgrade your plan for more companions.',
            ),
          );
          navigate('/subscription', { replace: true });
          return;
        }
      } catch (error) {
        // Ignore — let them proceed
      }
    };
    checkCompanionLimit();
  }, [navigate, t]);

  const [nameError, setNameError] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const handleContinue = async () => {
    if (!name.trim()) return;
    setNameError('');
    setIsChecking(true);

    try {
      // Check if name is already taken
      const res = await axios.get('/companions');
      const duplicate = res.data?.find(
        (c: any) => c.name.toLowerCase() === name.trim().toLowerCase(),
      );
      if (duplicate) {
        setNameError(
          t(
            'companionCreation.nameTaken',
            'You already have a companion with this name. Please choose a different one.',
          ),
        );
        setIsChecking(false);
        return;
      }
      sessionStorage.setItem('companionName', name);
      navigate('/companion/gender');
    } catch (error) {
      // If check fails, proceed anyway — backend will catch it
      sessionStorage.setItem('companionName', name);
      navigate('/companion/gender');
    } finally {
      setIsChecking(false);
    }
  };

  const handleSkip = () => {
    // Use default name
    sessionStorage.setItem('companionName', t('companionCreation.defaultName', 'Companion'));
    navigate('/companion/gender');
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <div className="pt-safe px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Progress indicator - 5 steps, step 1 active */}
          <div className="flex space-x-2 mb-8">
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
            {t('companionCreation.nameTitle', 'What would you like to call your companion?')}
          </h1>
          <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-8">
            {t('companionCreation.nameSubtitle', 'Choose a name that feels right to you')}
          </p>

          {/* Input */}
          <div className="mb-8">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('companionCreation.namePlaceholder', 'Enter a name...')}
              className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              maxLength={30}
              autoFocus
            />
            <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mt-2">
              {t('companionCreation.characterCount', '{{count}}/30 characters', {
                count: name.length,
              })}
            </p>
            {nameError && <p className="text-xs text-red-500 mt-2">{nameError}</p>}
          </div>

          {/* Suggestions */}
          <div className="mb-auto">
            <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-3">
              {t('companionCreation.suggestions', 'Suggestions:')}:
            </p>
            <div className="flex flex-wrap gap-2">
              {['Luna', 'Atlas', 'Nova', 'Sage', 'Echo', 'River'].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setName(suggestion)}
                  className="px-4 py-2 bg-dark-surface dark:bg-dark-surface bg-light-surface rounded-full text-dark-text dark:text-dark-text text-light-text text-sm hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleContinue}
              disabled={!name.trim() || isChecking}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isChecking ? t('common.checking', 'Checking...') : t('common.continue', 'Continue')}
            </button>
            <button onClick={handleSkip} className="btn-secondary">
              {t('companionCreation.skipForNow', 'Skip for now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionName;
